import Cookies from 'js-cookie';

interface BearerToken {
	token: string;
	tokenExpiration: string;
	refreshToken: string;
	refreshTokenExpiration: string;
	customerId: number;
	customer: {
		name: string;
		customerNumber: number;
	};
}

interface StoredTokenData {
	token: string;
	customer: {
		name: string;
		customerNumber: number;
	};
	customerId: number;
	expiresAt: string;
}

interface StoredRefreshTokenData {
	refreshToken: string;
	customerId: number;
	customerNumber: number;
	expiresAt: string;
	customerName?: string; // Preserve customer name for refresh
}

type TokenChangeListener = () => void;

export class TokenService {
	private static readonly TOKEN_COOKIE_NAME = 'qstar_auth_token';
	private static readonly REFRESH_TOKEN_KEY = 'qstar_refresh_token';
	private static readonly tokenChangeListeners = new Set<TokenChangeListener>();

	static subscribe(listener: TokenChangeListener): () => void {
		this.tokenChangeListeners.add(listener);

		return () => {
			this.tokenChangeListeners.delete(listener);
		};
	}

	private static isValidDate(date: Date): boolean {
		return Number.isFinite(date.getTime());
	}

	private static notifyTokenChange(): void {
		this.tokenChangeListeners.forEach(listener => listener());
	}

	/**
	 * Save authentication token with expiry
	 */
	static saveToken(bearerToken: BearerToken): void {
		// Use the actual token expiration from the API
		const expiresAt = new Date(bearerToken.tokenExpiration);
		if (!this.isValidDate(expiresAt)) {
			throw new Error('Invalid access token expiration');
		}

		const refreshExpiresAt = new Date(bearerToken.refreshTokenExpiration);
		if (bearerToken.refreshToken && !this.isValidDate(refreshExpiresAt)) {
			throw new Error('Invalid refresh token expiration');
		}

		const tokenData: StoredTokenData = {
			token: bearerToken.token,
			customer: bearerToken.customer,
			customerId: bearerToken.customerId,
			expiresAt: expiresAt.toISOString()
		};

		// Set cookie with expiration for access token
		Cookies.set(this.TOKEN_COOKIE_NAME, JSON.stringify(tokenData), {
			expires: expiresAt,
			path: '/',
			secure: import.meta.env.PROD, // Only secure in production
			sameSite: 'strict'
		});

		// Save refresh token in sessionStorage (lost when browser/tab closes)
		if (bearerToken.refreshToken) {
			const refreshTokenData: StoredRefreshTokenData = {
				refreshToken: bearerToken.refreshToken,
				customerId: bearerToken.customerId,
				customerNumber: bearerToken.customer.customerNumber,
				expiresAt: bearerToken.refreshTokenExpiration,
				customerName: bearerToken.customer?.name // Preserve for refresh
			};
			sessionStorage.setItem(this.REFRESH_TOKEN_KEY, JSON.stringify(refreshTokenData));
			console.log(`Refresh token saved, expires at:`, new Date(bearerToken.refreshTokenExpiration).toLocaleString());
		} else {
			console.warn('[Auth] No refresh token returned; automatic token refresh is unavailable for this session.');
		}

		console.log(`Token saved for ${bearerToken.customer.name}, expires at:`, expiresAt.toLocaleString());
		console.info('[Auth] Token storage metadata:', {
			hasAccessToken: Boolean(bearerToken.token),
			hasRefreshToken: Boolean(bearerToken.refreshToken),
			accessTokenExpiresAt: expiresAt.toISOString(),
			refreshTokenExpiresAt: bearerToken.refreshToken ? refreshExpiresAt.toISOString() : null,
			refreshTokenStored: Boolean(sessionStorage.getItem(this.REFRESH_TOKEN_KEY))
		});
		this.notifyTokenChange();
	}

	/**
	 * Get stored authentication token
	 */
	static getToken(): string | null {
		try {
			const cookieData = Cookies.get(this.TOKEN_COOKIE_NAME);
			if (!cookieData) return null;

			const tokenData: StoredTokenData = JSON.parse(cookieData);
			
			// Check if token is expired
			const expiresAt = new Date(tokenData.expiresAt);
			const now = new Date();
			
			if (!this.isValidDate(expiresAt) || now >= expiresAt) {
				// Only remove the access token — preserve the refresh token so auto-refresh can work
				Cookies.remove(this.TOKEN_COOKIE_NAME, { path: '/' });
				return null;
			}

			return tokenData.token;
		} catch (error) {
			console.error('Error parsing token cookie:', error);
			Cookies.remove(this.TOKEN_COOKIE_NAME, { path: '/' });
			return null;
		}
	}

	/**
	 * Get stored customer information
	 */
	static getCustomer(): { name: string; customerNumber: number } | null {
		try {
			const cookieData = Cookies.get(this.TOKEN_COOKIE_NAME);
			if (!cookieData) return null;

			const tokenData: StoredTokenData = JSON.parse(cookieData);
			
			// Check if token is expired
			const expiresAt = new Date(tokenData.expiresAt);
			const now = new Date();
			
			if (!this.isValidDate(expiresAt) || now >= expiresAt) {
				Cookies.remove(this.TOKEN_COOKIE_NAME, { path: '/' });
				return null;
			}

			return tokenData.customer;
		} catch (error) {
			console.error('Error parsing token cookie:', error);
			Cookies.remove(this.TOKEN_COOKIE_NAME, { path: '/' });
			return null;
		}
	}

	/**
	 * Check if user is authenticated
	 */
	static isAuthenticated(): boolean {
		return this.getToken() !== null;
	}

	/**
	 * Clear authentication token
	 */
	static clearToken(): void {
		Cookies.remove(this.TOKEN_COOKIE_NAME, { path: '/' });
		sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
		console.log('Token cleared - user logged out');
		this.notifyTokenChange();
	}

	/**
	 * Get stored refresh token
	 */
	static getRefreshToken(): string | null {
		try {
			const refreshData = sessionStorage.getItem(this.REFRESH_TOKEN_KEY);
			if (!refreshData) return null;

			const tokenData: StoredRefreshTokenData = JSON.parse(refreshData);
			
			// Check if refresh token is expired
			const expiresAt = new Date(tokenData.expiresAt);
			const now = new Date();
			
			if (!this.isValidDate(expiresAt) || now >= expiresAt) {
				console.log('Refresh token expired, removing...');
				sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
				return null;
			}

			return tokenData.refreshToken;
		} catch (error) {
			console.error('Error parsing refresh token:', error);
			sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
			return null;
		}
	}

	/**
	 * Get refresh token expiry time
	 */
	static getRefreshTokenExpiry(): Date | null {
		try {
			const refreshData = sessionStorage.getItem(this.REFRESH_TOKEN_KEY);
			if (!refreshData) return null;

			const tokenData: StoredRefreshTokenData = JSON.parse(refreshData);
			const expiresAt = new Date(tokenData.expiresAt);
			return this.isValidDate(expiresAt) ? expiresAt : null;
		} catch {
			return null;
		}
	}

	/**
	 * Check if we have a valid refresh token available
	 */
	static hasValidRefreshToken(): boolean {
		return this.getRefreshToken() !== null;
	}

	/**
	 * Get customer name from refresh token data (for use during token refresh)
	 */
	static getStoredCustomerName(): string | null {
		try {
			const refreshData = sessionStorage.getItem(this.REFRESH_TOKEN_KEY);
			if (!refreshData) return null;

			const tokenData: StoredRefreshTokenData = JSON.parse(refreshData);
			return tokenData.customerName || null;
		} catch {
			return null;
		}
	}

	/**
	 * Get customer information preserved with the refresh token.
	 */
	static getStoredCustomer(): { name: string; customerNumber: number } | null {
		try {
			const refreshData = sessionStorage.getItem(this.REFRESH_TOKEN_KEY);
			if (!refreshData) return null;

			const tokenData: StoredRefreshTokenData = JSON.parse(refreshData);
			if (!tokenData.customerName) {
				return null;
			}

			return {
				name: tokenData.customerName,
				customerNumber: Number.isFinite(tokenData.customerNumber)
					? tokenData.customerNumber
					: tokenData.customerId
			};
		} catch {
			return null;
		}
	}

	/**
	 * Get token expiry time
	 */
	static getTokenExpiry(): Date | null {
		try {
			const cookieData = Cookies.get(this.TOKEN_COOKIE_NAME);
			if (!cookieData) return null;

			const tokenData: StoredTokenData = JSON.parse(cookieData);
			const expiresAt = new Date(tokenData.expiresAt);
			return this.isValidDate(expiresAt) ? expiresAt : null;
		} catch {
			return null;
		}
	}

	/**
	 * Check if token will expire soon (within 10 minutes)
	 */
	static isTokenExpiringSoon(): boolean {
		const expiry = this.getTokenExpiry();
		if (!expiry) return false;

		const now = new Date();
		const tenMinutesFromNow = new Date(now.getTime() + 10 * 60 * 1000);
		
		return expiry <= tenMinutesFromNow;
	}
}